import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "./Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./Dialog";
import { Icon, SpinnerIcon } from "./icon";

interface DeleteClusterButtonProps {
  clusterId: string;
  onDelete?: () => void;
}

export function DeleteClusterButton({
  clusterId,
  onDelete,
}: DeleteClusterButtonProps) {
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteCluster = useCallback(async () => {
    if (deleteConfirmationInput !== clusterId) {
      setDeleteError("Cluster name confirmation does not match");
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await fetch(`/api/clusters/${clusterId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to delete cluster");
      }

      // Call optional onDelete callback
      if (onDelete) {
        onDelete();
      } else {
        // Default behavior: navigate back to main page with success message
        navigate("/", {
          state: {
            flash: `Cluster "${clusterId}" has been deleted successfully`,
          },
        });
      }
    } catch (err: any) {
      setDeleteError(err.message || "Failed to delete cluster");
    } finally {
      setIsDeleting(false);
    }
  }, [clusterId, deleteConfirmationInput, navigate, onDelete]);

  const handleDialogClose = () => {
    setIsDeleteDialogOpen(false);
    setDeleteConfirmationInput("");
    setDeleteError(null);
  };

  return (
    <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
      <DialogTrigger asChild>
        <Button
          variant="destructive"
          size="icon"
          className="h-8 w-8"
          title="Delete cluster"
        >
          <Icon name="Trash2" size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="outline-none md:max-w-2xl">
        <DialogHeader className="pt-1">
          <DialogTitle>Delete Cluster</DialogTitle>
        </DialogHeader>

        <div className="mb-4 rounded-md bg-red-50 p-3">
          <p className="text-sm text-red-800">
            <strong>Warning:</strong> This action cannot be undone. This will
            mark the cluster as deleted.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm text-gray-700">
              Please type the cluster name{" "}
              <strong className="font-mono text-red-600">{clusterId}</strong> to
              confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmationInput}
              onChange={(e) => {
                setDeleteConfirmationInput(e.target.value);
                setDeleteError(null);
              }}
              placeholder={clusterId}
              className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                deleteConfirmationInput === clusterId
                  ? ""
                  : "focus:ring-red-500"
              }`}
              autoComplete="off"
            />
            {deleteError ? (
              <p className="mt-2 text-sm text-red-600">{deleteError}</p>
            ) : null}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleDialogClose}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCluster}
              disabled={isDeleting || deleteConfirmationInput !== clusterId}
            >
              {isDeleting ? (
                <>
                  <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Cluster"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
